import { isEqual } from 'lodash'
import debounce from 'lodash.debounce'
import { useEffect, useRef, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { fetchGrandTotal, setCheckoutLoading } from './checkoutFunctions'
import { isThirdPartyBillingSelected } from './ShippingMethodSelector/shippingGroupsHelpers'

const useRecalculateGrandTotal = (isCheckout = true) => {
  const dispatch = useDispatch()
  const lastFetchedValues = useRef(null)
  const activeFetchId = useRef(null)

  const subtotal = useSelector(state => state.checkout.subtotal)
  const cartItems = useSelector(state => state.cartItems)
  const checkout = useSelector(state => state.checkout)
  const shippingPaymentMethod = useSelector(state => state.checkoutRates?.shippingPaymentMethod)
  const {
    taxQuote,
    shippingQuote,
    creditsUsed,
    appliedPromotions,
    appliedCoupon,
    subtotalLoading,
    subtotalCalculated
  } = checkout

  // If third party billing is selected, shipping cost should be 0
  const effectiveShippingQuote = isThirdPartyBillingSelected(shippingPaymentMethod) ? 0 : shippingQuote

  const fetchTotal = useCallback(
    (values, shouldClearPromotions) => {
      // Clear promotions when on cart page (not checkout)
      if (shouldClearPromotions) {
        dispatch({ type: 'CLEAR_PROMOTIONS' })
      }

      dispatch(setCheckoutLoading(true))

      const debouncedFetch = debounce(vals => {
        const fetchId = Symbol()
        activeFetchId.current = fetchId

        dispatch(
          fetchGrandTotal(
            vals.subtotal,
            vals.taxQuote,
            vals.shippingQuote,
            vals.creditsUsed,
            vals.appliedPromotions,
            vals.appliedCoupon,
            vals.cartItems
          )
        ).finally(() => {
          if (activeFetchId.current === fetchId) {
            dispatch(setCheckoutLoading(false))
          }
        })
      }, 800)

      debouncedFetch(values)
    },
    [dispatch]
  )

  useEffect(() => {
    // Wait for subtotal calculation to complete before fetching grand total
    if (subtotalLoading || !subtotalCalculated) {
      return
    }

    const currentValues = {
      subtotal,
      taxQuote,
      shippingQuote: effectiveShippingQuote,
      creditsUsed,
      appliedPromotions,
      appliedCoupon,
      cartItems
    }

    // Use deep comparison to prevent unnecessary API calls for object dependencies
    if (lastFetchedValues.current && isEqual(currentValues, lastFetchedValues.current)) {
      return
    }

    lastFetchedValues.current = currentValues
    fetchTotal(currentValues, !isCheckout && appliedPromotions.length > 0)
  }, [
    subtotal,
    taxQuote,
    effectiveShippingQuote,
    creditsUsed,
    appliedPromotions,
    appliedCoupon,
    cartItems,
    subtotalLoading,
    subtotalCalculated,
    fetchTotal,
    isCheckout
  ])
}

export default useRecalculateGrandTotal
